<script setup lang="ts">
  import { computed } from 'vue';

  import { EmailStatus } from '@stuut/api/models';
  import type { Email, EmailThread } from '@stuut/api/models';
  import { indexOf } from '@stuut/common/src/utils';
  import { cn } from '@ui/utils/utils.ts';

  import { EmailView } from '@/components/emails';
  import { useProfile } from '@/store';

  interface Props {
    /** Highlights newly-arrived emails with a fade animation. */
    newEmailIds?: Set<string>;
    /** Thread is processing — the last email shows a processing state. */
    processing?: boolean;
    /** Read-only view (triage): disables reply affordances on every email. */
    readonly?: boolean;
    /** Keeps this email expanded on first render even when it isn't the latest. */
    selectedEmailId?: string;
    size?: '2xs' | 'xs' | 'sm';
    thread: EmailThread;
  }

  const props = withDefaults(defineProps<Props>(), {
    size: 'sm',
  });

  const emits = defineEmits<{
    (e: 'draftCreated', email: Email): void;
    (e: 'updated'): void;
    (e: 'deleted', email: Email): void;
  }>();

  const profileStore = useProfile();
  const profile = computed(() => profileStore.profile);

  const lastEmailId = computed<string>(
    () => props.thread.emails?.[props.thread.emails.length - 1]?.id ?? '',
  );

  // Emails that have a draft reply beneath them — these start expanded so the
  // message being replied to is visible alongside its draft editor.
  const emailIdsWithDraftReply = computed<Set<string>>(() => {
    const ids = new Set<string>();
    for (const email of props.thread.emails ?? []) {
      if (!email.isSent && email.parent_email_id) ids.add(email.parent_email_id);
    }
    return ids;
  });

  const emails = computed<Email[]>(() => {
    const drafts: Email[] = [];
    const sortedEmails: Email[] = (props.thread.emails ?? []).filter((email) => {
      if (!email.isSent) drafts.push(email);

      return email.isSent;
    });

    drafts.forEach((draft) => {
      const parentIdx = indexOf(sortedEmails, (email) => email.id === draft.parent_email_id);
      if (parentIdx !== -1) {
        sortedEmails.splice(parentIdx + 1, 0, draft);
      } else {
        sortedEmails.push(draft);
      }
    });

    return profile.value?.dashboard_settings?.thread_emails_sort_order === 'desc'
      ? sortedEmails.toReversed()
      : sortedEmails;
  });

  // Two reply drafts belong to the same on-screen group when they're consecutive replies to the
  // same parent email — used to keep them inside one shared container, each its own card with a spacer between.
  const isReplyDraft = (email?: Email) => !!email && !email.isSent && !!email.parent_email_id;
  const sharesReplyGroup = (a?: Email, b?: Email) =>
    isReplyDraft(a) && isReplyDraft(b) && a?.parent_email_id === b?.parent_email_id;

  const isProcessingEmail = (email: Email) => !!props.processing && email.id === lastEmailId.value;
</script>

<template>
  <div class="flex flex-col w-full overflow-x-hidden">
    <template v-for="(email, idx) in emails" :key="email.id">
      <EmailView
        :id="email.id"
        :email="email"
        :size="size"
        :thread="thread"
        :hide-reply="readonly"
        :processing="isProcessingEmail(email)"
        :is-initial-draft="emails.length === 1 && email.isDraft"
        :merge-down="sharesReplyGroup(emails[idx + 1], email)"
        :initial-collapse="
          email.status !== EmailStatus.DRAFT &&
          email.id !== selectedEmailId &&
          idx + 1 !== emails.length &&
          !emailIdsWithDraftReply.has(email.id)
        "
        :class="
          cn(
            'border-t-0 border-r-1 border-b-0 border-border bg-background',
            idx === 0 && 'rounded-t-md border-t',
            idx > 0 && !email.isDraft && 'border-t-1',
            idx + 1 === emails.length && 'border-b-1 rounded-b-md',
            emails?.[idx - 1]?.isDraft &&
              !sharesReplyGroup(emails[idx - 1], email) &&
              'rounded-t-md',
            newEmailIds?.has(email.id) && 'animate-new-email',
          )
        "
        @draft-created="(e) => emits('draftCreated', e)"
        @updated="emits('updated')"
        @deleted="emits('deleted', email)"
      >
        <template #after_body="{ isCollapsed }">
          <slot
            name="after_body"
            v-bind="{ email, emails, isCollapsed, processing: isProcessingEmail(email) }"
          />
        </template>
      </EmailView>
    </template>
  </div>
</template>

<style lang="postcss" scoped>
  @keyframes new-email-fade {
    from {
      background-color: var(--warning-muted);
    }
    to {
      background-color: var(--color-card);
    }
  }

  .animate-new-email {
    animation: new-email-fade 1500ms ease-out forwards;
  }
</style>
